import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import ReportFooter from '../../../shared/ReportFooter'
import ReportHeader from '../../../shared/ReportHeader'
import { emrPatientPracDetailsList } from '../../../constants/EmrContants'
import { handlePrintData } from '../../../utils/PrintData'
import { PrintIcon } from '../../../utils/TableActionIcons'
import { InfoTable } from './Prescription'

const CATEGORY_ALIASES = {
  laboratory: ['laboratory', 'lab'],
  imaging: ['imaging', 'radiology', 'radiology/imaging']
}

const normalizeCategory = value =>
  String(value || '')
    .trim()
    .toLowerCase()

export const isClinicalOrderItem = (item, reportType) => {
  const aliases = CATEGORY_ALIASES[reportType] || []
  const categoryValues = [
    item?.selected_category,
    item?.item_type,
    item?.item_category,
    item?.category,
    item?.category_type
  ].map(normalizeCategory)

  return categoryValues.some(value => aliases.includes(value))
}

export const getClinicalOrderItems = (items, reportType) =>
  (Array.isArray(items) ? items : []).filter(item =>
    isClinicalOrderItem(item, reportType)
  )

const getItemName = (item, language) => {
  const name =
    language === 'ar'
      ? item?.non_standard_description_ar ||
        item?.item_name ||
        item?.standard_description_ar
      : item?.non_standard_description ||
        item?.item_name ||
        item?.standard_description

  return name || item?.name || item?.service_name || item?.description || '--'
}

const getItemCode = item =>
  item?.item_code ||
  item?.code ||
  item?.non_standard_code ||
  item?.service_code ||
  '--'

function ClinicalOrderReport ({ emrData, reportType }) {
  const printRef = useRef(null)
  const { t, i18n } = useTranslation()
  const items = useMemo(
    () => getClinicalOrderItems(emrData?.items, reportType),
    [emrData?.items, reportType]
  )
  const isImaging = reportType === 'imaging'
  const reportName = isImaging
    ? t('Radiology / Imaging Order')
    : t('Laboratory Order')

  return (
    <div>
      <div
        className={`icon-bg-container absolute top-[21px] ${
          i18n?.language === 'ar' ? 'left-[70px]' : 'right-[70px]'
        }`}
        onClick={() => handlePrintData(printRef.current?.innerHTML, reportName)}
      >
        <PrintIcon iconSize='20' />
      </div>

      <div className='print-container' ref={printRef}>
        <div
          className='flex min-h-[100vh] flex-col gap-0.5'
          style={{ fontSize: '12px' }}
        >
          <div>
            <ReportHeader reportName={reportName} />
            <InfoTable
              title={t('Patient Information')}
              rows={emrPatientPracDetailsList(emrData, t)}
            />

            <div className='mt-2 overflow-hidden rounded border border-gray-300 shadow-sm'>
              <div className='bg-gray-100 px-2 py-1'>
                <span className='text-[14px] !font-semibold'>
                  {isImaging
                    ? t('Imaging Items')
                    : t('Laboratory Items')}
                </span>
              </div>
              <table
                className='min-w-full border-collapse'
                style={{ fontSize: '12px' }}
              >
                <thead>
                  <tr>
                    <th className='w-10 border p-1 text-center !font-semibold'>
                      #
                    </th>
                    <th className='w-28 border p-1 text-start !font-semibold'>
                      {t('Code')}
                    </th>
                    <th className='border p-1 text-start !font-semibold'>
                      {t('Item')}
                    </th>
                    <th className='w-[42%] border p-1 text-start !font-semibold'>
                      {t('Clinical Notes')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={item?.item_id || item?.id || index}>
                      <td className='border p-1 text-center'>{index + 1}</td>
                      <td className='border p-1'>{getItemCode(item)}</td>
                      <td className='border p-1'>{getItemName(item, i18n?.language)}</td>
                      <td className='whitespace-pre-wrap border p-1'>
                        {item?.clinical_notes || '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='mt-4 flex w-full justify-end gap-4 print:mt-8'>
              <div className='text-center'>
                <p className='mt-0.5 text-[11px]'>{t('Practitioner Stamp')}</p>
                <img
                  src={emrData?.practitioner?.stamp}
                  alt={t('stamp')}
                  className='h-[5rem] w-[5rem] border-gray-200'
                />
              </div>
              <div className='text-center'>
                <p className='mt-0.5 text-[11px]'>
                  {t('Practitioner Signature')}
                </p>
                <img
                  src={emrData?.practitioner?.signature}
                  alt={t('signature')}
                  className='h-[5rem] w-[5rem] border-gray-200'
                />
              </div>
            </div>
          </div>

          <div className='mt-auto'>
            <ReportFooter />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClinicalOrderReport
